import { LineChart } from "echarts/charts";
import {
  GridComponent,
  TooltipComponent,
  TitleComponent,
  DatasetComponent,
  LegendComponent,
  DataZoomComponent,
  AriaComponent,
  TimelineComponent,
} from "echarts/components";
import { SVGRenderer } from "echarts/renderers";
import { UniversalTransition, LabelLayout } from "echarts/features";
import * as echarts from "echarts/core";
import ReactEChartsCore from "echarts-for-react/lib/core";
import React, { useEffect, useRef, useState } from "react";
import { showToast } from "components/Toast/toast.slice";
import store from "app/store";
import { useHistory } from "react-router-dom";
import { getQaAutomationReport } from "services/api/GetQaAutomationReportsApi";

// Automation Graph Data without Forecast
interface AutomationGraphData {
  xAxis: string[];
  totalAutomated: any[];
  totalPending: any[];
  totalCurrentCompleted: any[];
}

interface AutomationGraphValue {
  week: string[];
  totalAutomated: number[];
  totalPending: number[];
  totalCurrentCompleted: number[];
}
const LineAutomationGraph: any = (props: any) => {
  // Automation Graph related JS
  const { projectName, startDate, endDate, graphType } = props;
  const [automationGraphData, setAutomationGraphData] =
    useState<AutomationGraphData>({
      xAxis: [
        "week1",
        "week2",
        "week3",
        "week4",
        "week5",
        "week6",
        "week7",
        "week8",
      ],
      totalAutomated: [0, 20, 30, 50, 52, 40, 55, 44, 66],
      totalPending: [0, 15, 30, 80, 52, 78, 98, 130],
      totalCurrentCompleted: [0, 20, 30, 50, 52, 60, 76, 44, 4],
    });
  const setOptionsAutomation = () => {
    return {
      baseOption: {
        aria: {
          enabled: true,
          label: {
            enabled: true,
          },
        },
        tooltip: {
          trigger: "item",
          position: "top",
          backgroundColor: "#000000",
          textStyle: {
            color: "#FFFFFF",
            fontSize: 10,
          },
          axisPointer: {
            type: "cross",
          },
          extraCssText: "z-index: 1991 !important;",
        },
        legend: {
          orient: "horizontal",
          x: "center",
          y: "bottom",
          icon: "circle",
          textStyle: {
            fontFamily: "Montserrat",
            fontWeight: 500,
            color: "#222328",
            fontSize: 14,
          },
          itemGap: 20,
          lineHeight: 18,
          itemHeight: 9,
          itemWidth: 9,
          padding: 10,
        },
        grid: {
          top: "5%",
          left: "0%",
          right: "9%",
          bottom: "20%",
          containLabel: true,
        },
        xAxis: {
          data: automationGraphData.xAxis,
          type: "category",
          splitLine: {
            lineStyle: {
              width: 1,
              opacity: 0.5,
              color: "rgba(223, 223, 230, 1)",
            },
            show: true,
          },
          nameTextStyle: {
            fontFamily: " Montserrat",
            fontSize: 18,
            fontWeight: 600,
            lineHeight: 27,
            color: "#222328",
            align: "center",
            verticalAlign: "middle",
            overflow: "truncate",
            ellipsis: "...",
          },
          axisLabel: {
            formatter: (value: any) => {
              const newDates: any = `${value}`;
              return newDates;
            },
            interval: 0,
          },
          boundaryGap: false,
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
        },
        yAxis: {
          type: "value",
          axisLabel: {
            formatter(a: any) {
              a = +a;
              return Number.isFinite(a) ? echarts.format.addCommas(+a * 1) : "";
            },
          },
        },
        dataZoom: [
          {
            type: "inside",
            start: 0,
            end: 100,
          },
        ],
        textStyle: {
          fontFamily: " Montserrat",
          fontSize: 14,
          fontWeight: 600,
          lineHeight: 17,
          color: "#222328",
          align: "center",
          verticalAlign: "middle",
        },
        series: [
          {
            name: "Total Automated",
            type: "line",
            smooth: true,
            data: automationGraphData.totalAutomated,
            itemStyle: {
              color: "#707070",
            },
            emphasis: {
              focus: "self",
              // blurScope: "coordinateSystem",
            },
            lineStyle: {
              width: 2,
              type: "solid",
              cap: "round",
            },
          },
          {
            name: "Total Current Completed",
            type: "line",
            smooth: true,
            data: automationGraphData.totalCurrentCompleted,
            emphasis: {
              focus: "solid",
              // blurScope: "coordinateSystem",
            },
            itemStyle: {
              color: "#FCC503",
            },
            lineStyle: {
              width: 2,
              type: "solid",
              cap: "round",
            },
          },
          {
            name: "Total Pending",
            type: "line",
            smooth: true,
            data: automationGraphData.totalPending,
            emphasis: {
              focus: "self",
              // blurScope: "coordinateSystem",
            },
            itemStyle: {
              color: "#01A9A3",
            },
            lineStyle: {
              width: 2,
              type: "solid",
              cap: "round",
            },
          },
        ],
      },
    };
  };
  // COMMON CODE
  const [selectedWeeks, setSelectedWeeks] = useState<number>(4);
  const [loading, setLoading] = useState(true);
  const getCurrentEchartInstance = (chart: any) => {
    return chart.getEchartsInstance();
  };
  const history = useHistory();

  const chartDom2 = useRef(null);
  let chartInstance2: any = null;
  // Unforcasted Automation Graph
  async function renderChart2(chart: any) {
    const renderInstance = getCurrentEchartInstance(chart);
    if (renderInstance) {
      chartInstance2 = renderInstance;
    } else {
      chartInstance2 = echarts.init(chart);
    }
    if (
      automationGraphData.totalAutomated.reduce(
        (a: number, b: number) => a + b,
        0
      ) === 0 &&
      automationGraphData.totalCurrentCompleted.reduce(
        (a: any, b: any) => a + b,
        0
      ) === 0
    ) {
      renderInstance.setOption({
        title: {
          show: true,
          textStyle: {
            color: "grey",
            fontSize: 20,
          },
          text: "No data",
          left: "center",
          top: "center",
        },
      });
    } else {
      await chartInstance2.setOption({
        baseOption: {
          title: {
            show: false,
          },
          xAxis: {
            data: automationGraphData.xAxis,
          },
          series: [
            {
              name: "Total Automated",
              data: automationGraphData.totalAutomated,
            },
            {
              name: "Total Pending",
              data: automationGraphData.totalPending,
            },
            {
              name: "Total CurrentCompleted",
              data: automationGraphData.totalCurrentCompleted,
            },
          ],
        },
        // All EChart Supported Media Queries are relative to the size of the charts and not the current window size
        media: [
          {
            option: {
              series: [
                {
                  name: "Total Automated",
                  emphasis: {
                    disabled: false,
                    focus: "self",
                  },
                },
                {
                  name: "Total Pending",
                  emphasis: {
                    disabled: false,
                    focus: "self",
                  },
                },
                {
                  name: "Total Current Completed",
                  emphasis: {
                    disabled: false,
                    focus: "self",
                  },
                },
              ],
            },
          },
          {
            query: {
              maxWidth: 767,
            },
            option: {
              series: [
                {
                  name: "Total Automated",
                  emphasis: {
                    disabled: true,
                  },
                },
                {
                  name: "Total Pending",
                  emphasis: {
                    disabled: true,
                  },
                },
                {
                  name: "Total Current Completed",
                  emphasis: {
                    disabled: true,
                  },
                },
              ],
            },
          },
        ],
      });

      await chartInstance2.setOption({
        xAxis: {
          axisLabel: {
            formatter: (value: any) => {
              const newDates: any = new Date(`${value}`).toLocaleDateString(
                "en-US",
                {
                  day: "2-digit",
                  month: "short",
                }
              );
              return newDates;
            },
          },
        },
      });
    }
    setLoading(false);
  }
  const GetQaAutomationReportsApi = async () => {
    getQaAutomationReport({ projectName, startDate, endDate })
      .then(async (response: any) => {
        const { qaReports } = response.data;
        let xaxis: any[] = [];
        let yaxisTotalAutomated: any[] = [];
        let yaxisTotalPending: any[] = [];
        let yaxisTotalCurrentCompleted: any[] = [];
        qaReports.forEach((val: AutomationGraphValue) => {
          const newDates: any = new Date(`${val.week}`); // Change the date format as per your requirement
          xaxis = [...xaxis, newDates];
          yaxisTotalAutomated = [...yaxisTotalAutomated, val.totalAutomated];
          yaxisTotalPending = [...yaxisTotalPending, val.totalPending];
          yaxisTotalCurrentCompleted = [
            ...yaxisTotalCurrentCompleted,
            val.totalCurrentCompleted,
          ];
        });
        setAutomationGraphData({
          ...automationGraphData,
          xAxis: xaxis,
          totalAutomated: yaxisTotalAutomated,
          totalPending: yaxisTotalPending,
          totalCurrentCompleted: yaxisTotalCurrentCompleted,
        });
        setLoading(false);
      })
      .catch((error: any) => {
        setLoading(false);
        store.dispatch(
          showToast({
            type: "danger",
            title: "Error Occurred",
            message: error.message,
          })
        );
      });
  };
  // Use Effect to monitor and update the graph on change in dropdown
  useEffect(() => {
    // Axios request to hit endpoint
    setLoading(true);
    GetQaAutomationReportsApi();
  }, [startDate, endDate, projectName, graphType]);

  // Forcasted Automation Graph
  useEffect(() => {
    // Update chart values

    if (chartDom2.current !== null) {
      const lineChart = chartDom2.current;
      renderChart2(lineChart);
    }
    document
      .querySelector<any>(".echarts-for-react")
      ?.setAttribute("role", "img");
  }, [automationGraphData]);

  useEffect(() => {
    return () => {
      if (chartDom2.current !== null) {
        chartInstance2 = getCurrentEchartInstance(chartDom2.current);
      }
    };
  }, []);

  // Loading elements
  const loadingOption = {
    text: "loading",
    color: "#c23531",
    textColor: "#000",
    maskColor: "rgba(255, 255, 255, 0.8)",
    zlevel: 0,
    fontSize: 12,
    showSpinner: true,
    spinnerRadius: 10,
    lineWidth: 5,
    fontWeight: "normal",
    fontStyle: "normal",
  };

  useEffect(() => {
    // Update chart
    if (chartDom2.current !== null) {
      const chart = getCurrentEchartInstance(chartDom2.current);
    }
  }, [loading]);

  // Register the required components
  echarts.use([
    // CanvasRenderer,
    SVGRenderer,
    GridComponent,
    TooltipComponent,
    TitleComponent,
    DatasetComponent,
    LegendComponent,
    LineChart,
    UniversalTransition,
    DataZoomComponent,
    LabelLayout,
    AriaComponent,
    TimelineComponent,
  ]);

  return (
    <div>
      <ReactEChartsCore
        echarts={echarts}
        ref={chartDom2}
        option={setOptionsAutomation()}
        className="echart-wrapper transaction-log px-3 mx-2"
        loadingOption={loadingOption}
        showLoading={loading}
        opts={{ renderer: "svg" }}
        lazyUpdate
      />
    </div>
  );
};

export default LineAutomationGraph;
